import {
  AiAssistant,
  AiLesson,
  AiLessonCounts,
  AiLessonDraft,
  AiLessonGuidanceIssue,
  AiLessonMessageType,
  AiLessonTriggers,
  AiLessonVariants,
  EAiAssistantChannel,
  EAiLessonCategory,
  EAiLessonReplayStatus,
  EAiLessonStatus,
  EAiLessonTag
} from "../aiAgents.types";
import type {
  CreateLessonBody,
  UpdateLessonBody
} from "../apis/lessons/lessons.request";
import {
  AI_LESSON_GUIDANCE_RULES,
  AI_LESSON_LIMITS,
  AI_LESSON_MESSAGES,
  AI_LESSON_MESSAGE_TYPES,
  AI_LESSON_NON_DISCRIMINATING_TAGS,
  AI_LESSON_TAGS
} from "../constants/lessons";

// Pure lesson helpers: no React, no API client (the request import above is
// type-only), so they can be unit tested.

// Owner default: a Super admin may activate a lesson that was never replayed.
// Mirrors the backend's SUPER_ADMIN_CAN_SKIP_REPLAY; the server decides.
export const SUPER_ADMIN_CAN_SKIP_REPLAY = true;

const NOT_FOUND = 404;
// What Nest answers for a route it does not know. A lesson that does not exist
// is a 404 too, but carries its own domain exception name.
const ROUTE_NOT_FOUND_EXCEPTION = "NotFoundException";

// ---------------------------------------------------------------------------
// Matching form, copied from the backend matcher (toLessonMatchForm) so the
// front compares keywords exactly as the server does.

export const toLessonMatchForm = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Trimmed and lowercased, deduplicated by match form; accents are kept for
// display.
export const normaliseLessonKeywords = (keywords: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of keywords) {
    const keyword = raw.trim().toLowerCase();
    const form = toLessonMatchForm(keyword);
    if (!form || seen.has(form)) continue;
    seen.add(form);
    result.push(keyword);
  }
  return result;
};

export const hasDiscriminatingTag = (tags: EAiLessonTag[]): boolean =>
  tags.some((tag) => !AI_LESSON_NON_DISCRIMINATING_TAGS.includes(tag));

export const isLessonTag = (value: string): value is EAiLessonTag =>
  (AI_LESSON_TAGS as string[]).includes(value);

const isMessageType = (value: string): value is AiLessonMessageType =>
  (AI_LESSON_MESSAGE_TYPES as readonly string[]).includes(value);

const sortedKey = (values: string[]) => [...values].sort().join("\n");

// True when the change would bump the lesson's version and send it back to
// draft on the server.
export const triggersChanged = (
  before: AiLessonTriggers,
  after: AiLessonTriggers
): boolean =>
  sortedKey(before.tags) !== sortedKey(after.tags) ||
  sortedKey(before.messageTypes) !== sortedKey(after.messageTypes) ||
  sortedKey(normaliseLessonKeywords(before.keywords).map(toLessonMatchForm)) !==
    sortedKey(normaliseLessonKeywords(after.keywords).map(toLessonMatchForm));

// Same rule as the backend's lessonGuidanceEqual: whitespace differences are
// not a change (the injected block collapses them anyway).
const collapseSpaces = (value: string) => value.replace(/\s+/g, " ").trim();

export const guidanceChanged = (before: string, after: string): boolean =>
  collapseSpaces(before) !== collapseSpaces(after);

// ---------------------------------------------------------------------------
// Lessons tab (sub-tabs, activation gate, row actions).

export type LessonTab = "active" | "drafts" | "paused" | "archived";

export const lessonTabStatuses = (tab: LessonTab): EAiLessonStatus[] => {
  switch (tab) {
    case "active":
      return [EAiLessonStatus.Active];
    case "drafts":
      return [EAiLessonStatus.Draft, EAiLessonStatus.Tested];
    case "paused":
      return [EAiLessonStatus.Paused];
    case "archived":
      return [EAiLessonStatus.Archived];
  }
};

const ACTIVATABLE_STATUSES = new Set([
  EAiLessonStatus.Draft,
  EAiLessonStatus.Tested,
  EAiLessonStatus.Paused
]);

export const lessonCanActivate = (
  lesson: Pick<AiLesson, "status" | "replayStatus">,
  isSuper: boolean
): boolean =>
  ACTIVATABLE_STATUSES.has(lesson.status) &&
  (lesson.replayStatus === EAiLessonReplayStatus.Passed ||
    (isSuper && SUPER_ADMIN_CAN_SKIP_REPLAY));

export type LessonAction =
  | "edit"
  | "activate"
  | "pause"
  | "resume"
  | "replay"
  | "archive"
  | "restore"
  | "preview";

// What the status allows; whether activate/resume is enabled for this admin is
// lessonCanActivate.
export const lessonActionsFor = (
  lesson: Pick<AiLesson, "status">
): LessonAction[] => {
  switch (lesson.status) {
    case EAiLessonStatus.Draft:
    case EAiLessonStatus.Tested:
      return ["edit", "activate", "replay", "preview", "archive"];
    case EAiLessonStatus.Active:
      return ["edit", "pause", "replay", "preview", "archive"];
    case EAiLessonStatus.Paused:
      return ["edit", "resume", "replay", "preview", "archive"];
    case EAiLessonStatus.Archived:
      return ["restore"];
  }
};

export const LESSON_TABS: LessonTab[] = [
  "active",
  "drafts",
  "paused",
  "archived"
];

// Sub-tab badge; the counts cover the whole assistant, whatever the search.
export const lessonTabCount = (
  counts: AiLessonCounts | undefined,
  tab: LessonTab
): number =>
  counts
    ? lessonTabStatuses(tab).reduce(
        (sum, status) => sum + (counts[status] ?? 0),
        0
      )
    : 0;

export const lessonTotalCount = (counts: AiLessonCounts | undefined): number =>
  LESSON_TABS.reduce((sum, tab) => sum + lessonTabCount(counts, tab), 0);

// v1 is the WhatsApp sales assistant only. A backend that predates lessons
// sends no lessonsEnabled at all: the tab then stays hidden rather than
// calling routes that answer 404.
export const assistantSupportsLessons = (
  assistant: Pick<AiAssistant, "channel"> & { lessonsEnabled?: unknown }
): boolean =>
  assistant.channel === EAiAssistantChannel.WhatsApp &&
  typeof assistant.lessonsEnabled === "boolean";

// A Super admin activated it before any replay passed.
export const isActiveWithoutReplay = (
  lesson: Pick<AiLesson, "status" | "replayStatus">
): boolean =>
  lesson.status === EAiLessonStatus.Active &&
  lesson.replayStatus !== EAiLessonReplayStatus.Passed;

// The warning badge of an active lesson whose replay has not passed: judged
// "Not fixed" (an active lesson is not paused by that verdict, the switch is
// the owner's call), or never replayed (a Super admin activated it).
export const activeLessonReplayBadgeKey = (
  lesson: Pick<AiLesson, "status" | "replayStatus">
): string | null => {
  if (lesson.status !== EAiLessonStatus.Active) return null;
  switch (lesson.replayStatus) {
    case EAiLessonReplayStatus.Failed:
      return "aiLesson.activeReplayFailed";
    case EAiLessonReplayStatus.NotRun:
      return "aiLesson.activeWithoutReplay";
    default:
      return null;
  }
};

export interface LessonRowControls {
  // On/off switch, only for active and paused lessons (on = active). Turning
  // a paused lesson back on goes through the same gate as activating it.
  toggle: { checked: boolean; enabled: boolean } | null;
  // Draft or tested lessons. label is an i18n key.
  activate: { label: string; enabled: boolean } | null;
  edit: boolean;
  // Managers, on a lesson that is not archived. Disabled (tooltip = i18n key)
  // when the lesson has no source turn to replay.
  replay: { enabled: boolean; tooltip: string | null } | null;
  // Everyone, once a replay result exists (it is read-only).
  viewReplay: boolean;
  archive: boolean;
  restore: boolean;
  // Read-only and free, so it does not need the permission.
  preview: boolean;
}

// What one row of the Lessons tab shows. Without the permission every
// mutation is hidden; the server checks it again anyway.
export const lessonRowControls = (
  lesson: Pick<
    AiLesson,
    "status" | "replayStatus" | "sourceTurnMessageId" | "lastReplayResult"
  >,
  { isSuper, canManage }: { isSuper: boolean; canManage: boolean }
): LessonRowControls => {
  const actions = lessonActionsFor(lesson);
  const canActivate = lessonCanActivate(lesson, isSuper);
  const isOnOff =
    lesson.status === EAiLessonStatus.Active ||
    lesson.status === EAiLessonStatus.Paused;
  const skipsReplay =
    isSuper && lesson.replayStatus !== EAiLessonReplayStatus.Passed;

  return {
    toggle:
      canManage && isOnOff
        ? {
            checked: lesson.status === EAiLessonStatus.Active,
            enabled: lesson.status === EAiLessonStatus.Active || canActivate
          }
        : null,
    activate:
      canManage && actions.includes("activate")
        ? {
            label: skipsReplay
              ? "aiLesson.action.activateSuper"
              : "aiLesson.action.activate",
            enabled: canActivate
          }
        : null,
    edit: canManage && actions.includes("edit"),
    replay:
      canManage && actions.includes("replay")
        ? lesson.sourceTurnMessageId
          ? { enabled: true, tooltip: null }
          : { enabled: false, tooltip: "aiLesson.replayNoSource" }
        : null,
    viewReplay: Boolean(lesson.lastReplayResult),
    archive: canManage && actions.includes("archive"),
    restore: canManage && actions.includes("restore"),
    preview: actions.includes("preview")
  };
};

// Deep link that opens one conversation on the AI conversations page.
export const lessonConversationLink = (conversationId: string) => ({
  pathname: "/ai/conversations",
  search: `conversationId=${encodeURIComponent(conversationId)}`
});

// ---------------------------------------------------------------------------
// Lesson form.

export interface LessonFormValue {
  title: string;
  category: EAiLessonCategory;
  situation: string;
  // X: the reply being corrected. Never sent to the assistant.
  badReply: string;
  // Y: what it should have answered. Never sent to the assistant either.
  correctedReply: string;
  // The admin's note for the AI draft; not saved with the lesson.
  correction: string;
  // The only text the assistant will ever see.
  guidance: string;
  tags: EAiLessonTag[];
  keywords: string[];
  messageTypes: AiLessonMessageType[];
  variants: AiLessonVariants | null;
}

export const emptyLessonForm = (
  overrides: Partial<LessonFormValue> = {}
): LessonFormValue => ({
  title: "",
  category: EAiLessonCategory.Behaviour,
  situation: "",
  badReply: "",
  correctedReply: "",
  correction: "",
  guidance: "",
  tags: [],
  keywords: [],
  messageTypes: [],
  variants: null,
  ...overrides
});

export const toggleLessonTag = (
  tags: EAiLessonTag[],
  tag: EAiLessonTag
): EAiLessonTag[] => {
  if (tags.includes(tag)) return tags.filter((t) => t !== tag);
  if (tags.length >= AI_LESSON_LIMITS.TAGS.MAX) return tags;
  return AI_LESSON_TAGS.filter((t) => t === tag || tags.includes(t));
};

export type AddKeywordResult =
  | { ok: true; keywords: string[] }
  | { ok: false; reason: "invalid" | "duplicate" };

export const addLessonKeyword = (
  keywords: string[],
  raw: string
): AddKeywordResult => {
  const keyword = raw.trim().toLowerCase();
  const { MAX, MIN_LENGTH, MAX_LENGTH } = AI_LESSON_LIMITS.KEYWORDS;
  if (
    keyword.length < MIN_LENGTH ||
    keyword.length > MAX_LENGTH ||
    !toLessonMatchForm(keyword) ||
    keywords.length >= MAX
  ) {
    return { ok: false, reason: "invalid" };
  }
  const form = toLessonMatchForm(keyword);
  if (keywords.some((k) => toLessonMatchForm(k) === form)) {
    return { ok: false, reason: "duplicate" };
  }
  return { ok: true, keywords: [...keywords, keyword] };
};

// Fills the form from a draft-assist answer. What the admin already wrote for
// Y and the note is kept; the A/B choice starts over. Texts are cut to the
// form limits so a long draft can still be saved.
export const applyDraftToForm = (
  form: LessonFormValue,
  draft: AiLessonDraft,
  generatedAt: string
): LessonFormValue => {
  const { TITLE, SITUATION, GUIDANCE, TAGS, KEYWORDS } = AI_LESSON_LIMITS;
  const tags = AI_LESSON_TAGS.filter((tag) =>
    draft.tags.some((t) => t === tag)
  ).slice(0, TAGS.MAX);
  const keywords = normaliseLessonKeywords(draft.keywords).filter(
    (k) => k.length >= KEYWORDS.MIN_LENGTH && k.length <= KEYWORDS.MAX_LENGTH
  );
  return {
    ...form,
    title: draft.title.trim().slice(0, TITLE.MAX_LENGTH) || form.title,
    situation:
      draft.situation.trim().slice(0, SITUATION.MAX_LENGTH) || form.situation,
    category: Object.values(EAiLessonCategory).includes(draft.category)
      ? draft.category
      : EAiLessonCategory.Behaviour,
    guidance:
      draft.guidance.trim().slice(0, GUIDANCE.MAX_LENGTH) || form.guidance,
    tags: tags.length > 0 ? tags : form.tags,
    keywords: keywords.slice(0, KEYWORDS.MAX),
    variants: {
      a: draft.variantA.trim(),
      b: draft.variantB.trim(),
      chosen: null,
      edited: false,
      model: draft.model || null,
      generatedAt
    }
  };
};

export const chooseLessonVariant = (
  form: LessonFormValue,
  which: "a" | "b"
): LessonFormValue => {
  if (!form.variants) return form;
  return {
    ...form,
    correctedReply: form.variants[which],
    variants: { ...form.variants, chosen: which, edited: false }
  };
};

// Typing in Y after choosing A or B marks the choice as edited.
export const setLessonCorrectedReply = (
  form: LessonFormValue,
  correctedReply: string
): LessonFormValue => {
  const { variants } = form;
  if (!variants || variants.chosen === null) {
    return { ...form, correctedReply };
  }
  return {
    ...form,
    correctedReply,
    variants: {
      ...variants,
      edited: correctedReply !== variants[variants.chosen]
    }
  };
};

// Client-side checks with the server's own messages (they are i18n keys), so
// the admin sees what is missing before the request.
export const validateLessonForm = (form: LessonFormValue): string[] => {
  const errors: string[] = [];
  const { TITLE, SITUATION, REPLY, GUIDANCE, TAGS, CORRECTION_NOTE } =
    AI_LESSON_LIMITS;
  const title = form.title.trim();
  const guidance = form.guidance.trim();

  if (title.length < TITLE.MIN_LENGTH || title.length > TITLE.MAX_LENGTH) {
    errors.push(AI_LESSON_MESSAGES.TITLE_LENGTH);
  }
  if (form.situation.trim().length > SITUATION.MAX_LENGTH) {
    errors.push(AI_LESSON_MESSAGES.SITUATION_LENGTH);
  }
  if (
    form.badReply.length > REPLY.MAX_LENGTH ||
    form.correctedReply.trim().length > REPLY.MAX_LENGTH
  ) {
    errors.push(AI_LESSON_MESSAGES.REPLY_LENGTH);
  }
  if (
    guidance.length < GUIDANCE.MIN_LENGTH ||
    guidance.length > GUIDANCE.MAX_LENGTH
  ) {
    errors.push(AI_LESSON_MESSAGES.GUIDANCE_LENGTH);
  }
  if (form.correction.trim().length > CORRECTION_NOTE.MAX_LENGTH) {
    errors.push(AI_LESSON_MESSAGES.NOTE_LENGTH);
  }
  if (form.tags.length < TAGS.MIN || form.tags.length > TAGS.MAX) {
    errors.push(AI_LESSON_MESSAGES.TAGS_COUNT);
  } else if (!hasDiscriminatingTag(form.tags)) {
    errors.push(AI_LESSON_MESSAGES.NEEDS_DISCRIMINATING_TAG);
  }
  return errors;
};

export interface LessonSource {
  conversationId: string;
  turnMessageId: string;
  messageId: string;
}

// The triggers as they are sent to the server.
export const toLessonTriggers = (form: LessonFormValue): AiLessonTriggers => ({
  tags: form.tags,
  keywords: normaliseLessonKeywords(form.keywords),
  messageTypes: form.messageTypes.filter(isMessageType)
});

// The create body without the assistant id, which the mutation adds. The
// correction note is only for the AI draft and is not saved.
export const toCreateLessonBody = (
  form: LessonFormValue,
  source: LessonSource | null
): Omit<CreateLessonBody, "assistantId"> => ({
  category: form.category,
  title: form.title.trim(),
  situation: form.situation.trim(),
  badReply: form.badReply,
  correctedReply: form.correctedReply.trim(),
  guidance: form.guidance.trim(),
  triggers: toLessonTriggers(form),
  ...(form.variants ? { variants: form.variants } : {}),
  ...(source
    ? {
        sourceConversationId: source.conversationId,
        sourceTurnMessageId: source.turnMessageId,
        sourceMessageId: source.messageId
      }
    : {})
});

// Edit modal: the form starts from the saved lesson. Stored values the form
// does not know (an old tag, a malformed message type) are dropped.
export const lessonToFormValue = (lesson: AiLesson): LessonFormValue =>
  emptyLessonForm({
    title: lesson.title,
    category: Object.values(EAiLessonCategory).includes(lesson.category)
      ? lesson.category
      : EAiLessonCategory.Behaviour,
    situation: lesson.situation ?? "",
    badReply: lesson.badReply ?? "",
    correctedReply: lesson.correctedReply ?? "",
    guidance: lesson.guidance,
    tags: AI_LESSON_TAGS.filter((tag) =>
      (lesson.triggers?.tags ?? []).includes(tag)
    ),
    keywords: [...(lesson.triggers?.keywords ?? [])],
    messageTypes: AI_LESSON_MESSAGE_TYPES.filter((type) =>
      (lesson.triggers?.messageTypes ?? []).includes(type)
    ),
    variants: lesson.variants
  });

// True when saving would bump the version and send a tested, active or paused
// lesson back to draft (the edit modal warns before saving).
export const lessonEditResetsToDraft = (
  lesson: AiLesson,
  form: LessonFormValue
): boolean =>
  lesson.status !== EAiLessonStatus.Draft &&
  (guidanceChanged(lesson.guidance, form.guidance) ||
    triggersChanged(lesson.triggers, toLessonTriggers(form)));

// The update body with only what changed, or null when nothing did. X, Y and
// the A/B versions are read-only in the edit modal, so they are never sent.
export const toUpdateLessonBody = (
  lesson: AiLesson,
  form: LessonFormValue
): Omit<UpdateLessonBody, "assistantId"> | null => {
  const changes: Omit<UpdateLessonBody, "assistantId" | "lessonId"> = {};
  const title = form.title.trim();
  const situation = form.situation.trim();
  const triggers = toLessonTriggers(form);

  if (form.category !== lesson.category) changes.category = form.category;
  if (title !== lesson.title) changes.title = title;
  if (situation !== (lesson.situation ?? "")) changes.situation = situation;
  if (guidanceChanged(lesson.guidance, form.guidance)) {
    changes.guidance = form.guidance.trim();
  }
  if (triggersChanged(lesson.triggers, triggers)) changes.triggers = triggers;

  return Object.keys(changes).length > 0
    ? { lessonId: lesson.id, ...changes }
    : null;
};

// ---------------------------------------------------------------------------
// Requests.

// Query string with arrays as repeated keys (`statuses=a&statuses=b`), the
// form the backend's query parser reads; empty values are left out.
export const toRepeatedQueryString = (
  query: Record<string, unknown>
): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else {
      params.append(key, String(value));
    }
  });
  return params.toString();
};

// ---------------------------------------------------------------------------
// Server errors.

// i18n key of a guidance issue; an unknown rule id is shown as is.
export const guidanceIssueLabelKey = (rule: string): string =>
  (AI_LESSON_GUIDANCE_RULES as readonly string[]).includes(rule)
    ? `aiLesson.guidanceRule.${rule}`
    : rule;

interface ErrorLike {
  response?: {
    status?: number;
    data?: {
      exception?: unknown;
      message?: unknown;
      metadata?: { issues?: unknown } | null;
    } | null;
  };
}

const asErrorLike = (error: unknown): ErrorLike =>
  error !== null && typeof error === "object" ? (error as ErrorLike) : {};

// A route-level 404 on a lessons or turn route means the backend that serves
// it is not deployed yet: the feature is hidden instead of alerting. A domain
// 404 (a lesson that is gone) is a real error and is shown.
export const isEndpointMissingError = (error: unknown): boolean => {
  const response = asErrorLike(error).response;
  if (response?.status !== NOT_FOUND) return false;
  const exception = response.data?.exception;
  return exception == null || exception === ROUTE_NOT_FOUND_EXCEPTION;
};

const isGuidanceIssue = (value: unknown): value is AiLessonGuidanceIssue =>
  value !== null &&
  typeof value === "object" &&
  typeof (value as AiLessonGuidanceIssue).rule === "string" &&
  typeof (value as AiLessonGuidanceIssue).match === "string";

export interface LessonApiErrorDetails {
  endpointMissing: boolean;
  // Guidance words the validator refused (LessonGuidanceForbiddenTerms).
  issues: AiLessonGuidanceIssue[];
  // Backend messages, which are i18n keys.
  messages: string[];
}

// Nest prefixes a nested DTO's messages with its property path
// ("triggers.Unknown situation."); the i18n key is the sentence after it.
const VALIDATOR_PATH_PREFIX = /^(?:[a-z][A-Za-z0-9]*\.)+(?=[A-Z])/;

export const toLessonMessageKey = (message: string): string =>
  message.replace(VALIDATOR_PATH_PREFIX, "");

export const parseLessonApiError = (error: unknown): LessonApiErrorDetails => {
  const response = asErrorLike(error).response;
  const data = response?.data ?? null;
  const rawIssues = data?.metadata?.issues;
  const issues = Array.isArray(rawIssues)
    ? rawIssues.filter(isGuidanceIssue)
    : [];
  const rawMessage = data?.message;
  const messages = (Array.isArray(rawMessage) ? rawMessage : [rawMessage])
    .filter((m): m is string => typeof m === "string" && m.length > 0)
    .map(toLessonMessageKey);
  return {
    endpointMissing: isEndpointMissingError(error),
    issues,
    // Two nested fields can report the same sentence.
    messages: [...new Set(messages)]
  };
};
